import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ModalComponent } from "../design-system/modal/modal.component";
import { ModalConfig } from "../design-system/modal/modal.modal";

export type CcFreeTransactionsModalMode =
  /** A batch is being imported that is bigger than the free allowance. */
  | "import"
  /** Free package running low on transactions. */
  | "freeLowBalance"
  /** Paid package running low on transactions. */
  | "paidLowBalance"
  /** No package at all — nothing can be imported until the organisation subscribes. */
  | "noPackage";

/**
 * Alert raised when an organisation is out of, or running low on, credit-card
 * transaction credits. `mode` picks the wording and the actions:
 *
 * - `import` — shown at import time, offers importing the free allowance or subscribing.
 * - `freeLowBalance` / `paidLowBalance` — shown on the dashboard, subscribe only.
 *
 * The parent owns the visibility flag and any import; this component only renders
 * the alert and handles the subscribe navigation, which is the same everywhere.
 */
@Component({
  selector: "cc-free-transactions-modal",
  templateUrl: "./cc-free-transactions-modal.component.html",
  standalone: true,
  imports: [CommonModule, TranslateModule, ModalComponent],
})
export class CcFreeTransactionsModalComponent {
  /** Transactions the organisation still has. */
  @Input() remainingTransactions: number = 0;

  /** How many transactions the current file / feed batch holds. Import mode only. */
  @Input() transactionCount: number = 0;

  /** Balance the organisation is expected to stay above. Low balance modes only. */
  @Input() threshold: number = 0;

  @Input() set mode(value: CcFreeTransactionsModalMode) {
    this._mode = value;
    this.modalConfig = {
      ...this.modalConfig,
      title: this.fnGetTitle(value),
      size: value == "import" ? "sm" : "md",
    };
  }
  get mode(): CcFreeTransactionsModalMode {
    return this._mode;
  }
  private _mode: CcFreeTransactionsModalMode = "import";

  @Input() set visible(value: boolean) {
    this.modalConfig = { ...this.modalConfig, visible: value };
  }

  /** User chose to import only the free allowance. Import mode only. */
  @Output() importFreeTransactions = new EventEmitter<void>();

  /** User dismissed the alert. */
  @Output() closed = new EventEmitter<void>();

  modalConfig: ModalConfig = {
    visible: false,
    title: this.translate.instant("label_alert"),
    variant: "error",
    size: "sm",
  };

  constructor(private translate: TranslateService, private router: Router) {}

  private fnGetTitle(mode: CcFreeTransactionsModalMode): string {
    switch (mode) {
      case "freeLowBalance":
        return this.translate.instant("cc_transactions_running_low_title");
      case "paidLowBalance":
        return this.translate.instant("cc_transactions_import_paused_title");
      case "noPackage":
        return this.translate.instant("cc_transactions_subscribe_title");
      default:
        return this.translate.instant("label_alert");
    }
  }

  onVisibleChange(visible: boolean) {
    this.modalConfig = { ...this.modalConfig, visible: visible };
    if (!visible) {
      this.closed.emit();
    }
  }

  fnImportFreeTransactions() {
    this.importFreeTransactions.emit();
  }

  fnCompletePayment() {
    localStorage.setItem("IsfromCCCredits", "true");
    this.router.navigate(["/orgsetting/ccsetup"]);
  }
}
